import type { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { StyledInputContainer } from '@withjoy/joykit/components/InputV1/StyledInputContainer';
import { Placeholder } from '@withjoy/joykit/components/SelectV1/styles';
import { fieldFocusRing } from './fieldFocusRing';
import type { FieldFocusRingColors } from './fieldFocusRing';

type InputV1ToV2StylesOptions = Readonly<{
  isInvalid?: boolean;
  error?: string | null;
  isDisabled?: boolean;
  borderRadius?: number;
  hasIcon?: boolean;
  iconLeftOffset?: string;
  focusColor?: 'inputBorderFocused' | 'primary7';
  borderStrategy?: 'border' | 'outline';
  includeSelectWrapper?: boolean;
  includePlaceholder?: boolean;
}>;

/**
 * Overrides InputV1's StyledInputContainer to match InputV2's box-shadow focus ring.
 * Consolidation of 7+ duplicated definitions across admin/guest code.
 *
 * @param colors - Theme colors from `useTheme().colors`
 * @param options - Configuration for validation state, focus color, border strategy, etc.
 */
export function inputV1ToV2Styles(colors: FieldFocusRingColors, options: InputV1ToV2StylesOptions = {}): CSSObject {
  const {
    isInvalid = false,
    error,
    isDisabled = false,
    borderRadius,
    hasIcon = false,
    iconLeftOffset = '40px',
    focusColor = 'inputBorderFocused',
    borderStrategy = 'outline',
    includeSelectWrapper = false,
    includePlaceholder = false,
  } = options;

  const invalid = isInvalid || !!error;
  const ring = fieldFocusRing(colors, { isInvalid: invalid, isDisabled, focusColor });
  const radiusStyles: CSSObject = borderRadius != null ? { borderRadius: `${borderRadius}px` } : {};

  const containerBorder: CSSObject =
    borderStrategy === 'border' ? { border: invalid ? `2px solid ${colors.inputBorderError}` : '2px solid transparent' } : { outline: ring.errorOutline };

  const inputStyles: CSSObject = {
    outline: 0,
    _focus: { outline: 0 },
    fontSize: '1rem !important',
    ...radiusStyles,
    ...(hasIcon ? { paddingLeft: `${iconLeftOffset} !important` } : {}),
  };

  const result: CSSObject = {
    ['& > div']: { width: '100% !important' },
    [`${StyledInputContainer}`]: {
      ...containerBorder,
      ...radiusStyles,
      _after: {
        border: '0px solid transparent',
        ...radiusStyles,
        boxShadow: ring.afterShadow,
        content: '" "',
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
      },
      ['input']: inputStyles,
    },
  };

  if (includeSelectWrapper) {
    result['&[data-select-wrapper] > div'] = { width: '100%' };
    if (!isDisabled) {
      const focusOutlineColor = focusColor === 'primary7' ? colors.primary7 : colors.inputBorderFocused;
      // outline does not follow border-radius by default; pair it with a matching border-radius
      // so the focus ring hugs the field corners. Default to radii[3] (8px) which matches
      // SelectV1/InputV2's native border-radius.
      result._focusWithin = {
        outline: invalid ? `2px solid ${colors.inputBorderError}` : `2px solid ${focusOutlineColor}`,
        borderRadius: radiusStyles.borderRadius ?? 3,
      };
    }
  }

  if (includePlaceholder) {
    result[`${Placeholder}`] = {
      color: isDisabled ? 'inputPlaceholderDisabled' : '',
    };
  }

  return result;
}

/**
 * Safely extract StyledInputContainer sub-styles from an inputV1ToV2Styles result.
 * CSSObject's union type prevents direct indexed access with styled-component selectors;
 * this helper encapsulates the necessary type casts in one place.
 */
export function extractContainerStyles(styles: CSSObject): Readonly<{
  container: CSSObject;
  input: CSSObject;
  after: CSSObject;
}> {
  const record = styles as Record<string, CSSObject>;
  const container = record[`${StyledInputContainer}`] ?? ({} as CSSObject);
  const input = (container as Record<string, CSSObject>)['input'] ?? ({} as CSSObject);
  const after = (container._after as CSSObject) ?? ({} as CSSObject);
  return { container, input, after };
}

export type { InputV1ToV2StylesOptions };
export { StyledInputContainer };
